// The opportunity (Iris's screens 4 and 5): everything about one job's pursuit. The stage track (a tap moves it, with
// undo), the step on now with its seat, done-when, review link and controls, Hand to and Discuss, the values (tap to
// change), first visit and design lane, the jobs, the activity log, the Drive folder, Split. On the phone the same,
// stacked, the call to make first. Replaces the 2026-10 record screen (screens/Jobs.tsx); its sources and files stay in a
// fold at the bottom.

import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { dayAfter, landingStep, undoableEntry, type PipelineStage, type PipelineStep, type TapEntry } from '@zerogap/engine';
import { useCrumb } from '../../frame/Frame';
import { deleteRow, insertRow, refresh, rpc, toast, updateRow, useRereadOnReturn, useRows, type Row } from '../../lib/api';
import { contactName, longDate, money, monthDay, today } from '../../lib/format';
import { logTouch, lostRetouch, moveStage, PIPELINE_TABLES, saveLead, seatWords, setOutcome, undoTap, usePipeline, type Lead, type Pipeline } from '../../lib/pipeline';
import { useSession } from '../../lib/session';
import { Btn, Edit, Empty, Fold, Loading, Money, Panel, Tag, useFolds } from '../../ui/kit';
import { FileList } from '../job/Files';
import { NewOpportunity } from '../NewOpportunity';
import { BringBack, Discuss, HandTo, LeadValue, Likelihood, ReviewLine, SeatTag, StepControls, UndoRow, useTaps } from './bits';
import { SplitDialog } from './Split';

const SOURCE_KINDS = ['drawing', 'shop_drawing', 'site_dims', 'email', 'text', 'call', 'client_page', 'other'];
const kindWords = (k: unknown) => (k === 'client_page' ? 'client page' : String(k).replace(/_/g, ' '));
/** The page slug a client page link carries: the last part of its address, which the page's Send posts to the log. */
const slugOf = (url: unknown): string => {
  try {
    return new URL(String(url)).pathname.split('/').filter(Boolean).pop() ?? '';
  } catch {
    return String(url ?? '').split('/').filter(Boolean).pop() ?? '';
  }
};

export function OpportunityPage() {
  const { id } = useParams();
  const p = usePipeline();
  useRereadOnReturn(PIPELINE_TABLES);
  const lead = id ? p.byId.get(id) ?? null : null;
  useCrumb([{ label: 'Home', to: '/' }, { label: 'Pipeline', to: '/pipeline' }, ...(lead?.stage ? [{ label: lead.stage.name, to: `/pipeline/stage/${lead.stage.key}` }] : []), { label: lead?.name ?? 'Opportunity' }]);
  if (p.loading) return <Loading what="Loading the opportunity" />;
  if (!lead) return <Empty>This opportunity no longer exists.</Empty>;
  return <OpportunityScreen p={p} lead={lead} />;
}

function OpportunityScreen({ p, lead }: { p: Pipeline; lead: Lead }) {
  const s = useSession();
  const nav = useNavigate();
  const taps = useTaps();
  const folds = useFolds(`opp.${lead.id}`);
  const [addJob, setAddJob] = useState(false);
  const [splitting, setSplitting] = useState(false);
  const [allLog, setAllLog] = useState(false);
  // a tap on a client page's answer chip opens its log entry: the whole log on the phone, then the entry lit and in view
  const [litEntry, setLitEntry] = useState<string | null>(null);
  const showEntry = (id: string) => {
    setAllLog(true);
    setLitEntry(id);
    window.setTimeout(() => document.getElementById(`log-${id}`)?.scrollIntoView({ block: 'center', behavior: 'smooth' }), 80);
    window.setTimeout(() => setLitEntry((v) => (v === id ? null : v)), 4000);
  };
  const activity = useRows('activity', { eq: { opportunity_id: lead.id }, order: ['-at', '-created_at'] });
  const log = activity.rows;
  const [asking, setAsking] = useState<{ kind: 'move' | 'won' | 'park' | 'lost'; stage: PipelineStage; step: PipelineStep | null } | null>(null);
  const [askDate, setAskDate] = useState('');
  const [askWhy, setAskWhy] = useState('');
  const lostWeeks = s.setting<{ weeks?: number }>('pipeline_lost_retouch', {}).weeks ?? null;
  const users = p.users;
  const canEdit = s.canEdit('screen.pipeline');
  const phone = s.device === 'phone';
  const tap = taps.tap(lead.id);
  const prices = s.can('see_prices');
  const splitFrom = lead.opp.split_from_id ? p.byId.get(lead.opp.split_from_id) : null;
  const splitTo = p.leads.filter((l) => l.opp.split_from_id === lead.id);
  const seat = seatWords(p, lead.step);
  const save = (patch: Row) => saveLead(lead, patch);
  const todayStr = today();

  // the band over the step on now: gold when it waits on Spencer, plain when it is with someone else
  const band = lead.outcome !== 'open'
    ? { gold: false, words: lead.outcome === 'won' ? `Won ${monthDay(lead.opp.outcome_at)}` : lead.outcome === 'parked' ? `Parked · re-approach ${lead.nextTouch ? monthDay(lead.nextTouch) : 'not set'}` : lead.outcome === 'lost' ? `Lost ${monthDay(lead.opp.outcome_at)}${lead.opp.outcome_note ? ` · ${lead.opp.outcome_note}` : ''}` : 'Not a lead' }
    : !lead.step
      ? { gold: false, words: 'Not placed on a step yet' }
      : lead.needsOwner
        ? { gold: true, words: 'Waiting on you now' }
        : { gold: false, words: `With ${seat.words}${lead.replyBy ? ` · reply-by ${lead.replyBy === todayStr ? 'today' : monthDay(lead.replyBy)}` : ''}` };

  // a tap on the track only asks; the button under it moves (Spencer, 2026-10-22: one stray tap moved the Window Sill).
  // Park asks for the re-approach date and Lost for the reason in the same row: a park needs a next touch, a loss a reason.
  const trackMove = (stageKey: string) => {
    const st = p.stages.find((x) => x.key === stageKey);
    if (!st) return;
    if (st.kind === 'working') setAsking({ kind: 'move', stage: st, step: landingStep(p.steps, st.id, p.conditions, lead.opp, log as TapEntry[]) });
    else if (st.kind === 'won') setAsking({ kind: 'won', stage: st, step: null });
    else if (st.kind === 'parked') {
      setAskDate(lead.nextTouch ?? '');
      setAsking({ kind: 'park', stage: st, step: null });
    } else if (st.kind === 'lost') {
      setAskWhy(lead.opp.outcome_note ?? '');
      setAsking({ kind: 'lost', stage: st, step: null });
    }
  };
  const move = async () => {
    if (!asking) return;
    const { kind, stage, step } = asking;
    try {
      const tap =
        kind === 'won' ? await setOutcome(lead, 'won', null, null, stage.id)
        : kind === 'park' ? await setOutcome(lead, 'parked', null, askDate || null)
        : kind === 'lost' ? await setOutcome(lead, 'lost', askWhy.trim() || null, lostRetouch(lostWeeks))
        : await moveStage(lead, stage, step);
      setAsking(null);
      taps.remember(lead.id, tap);
    } catch {
      // the refusal is on the toast; the ask stays open
    }
  };

  // the newest tap stays undoable from the log after the screen is left (its author or the owner, as the database allows)
  const undoable = canEdit && !tap ? undoableEntry(log as TapEntry[]) : null;
  const mayUndo = undoable && (s.user?.role === 'owner' || (undoable as Row).by_user_id === s.user?.id) ? undoable : null;
  const undoFromLog = async () => {
    if (!mayUndo) return;
    taps.forget(lead.id);
    await undoTap(mayUndo.id);
  };

  const shownLog = phone && !allLog ? log.slice(0, 3) : log;

  const values = (
    <Panel>
      {canEdit && <div className="lb">Tap a value to change it</div>}
      <div className="pvals">
        <div className="pval">
          <span className="lb">Value</span>
          {lead.value.kind === 'priced' || !canEdit ? (
            <span className="pv-v">
              <LeadValue lead={lead} big />
            </span>
          ) : (
            <Edit className="pv-v cell num" value={lead.opp.typed_value_cents ? `$${Math.round(Number(lead.opp.typed_value_cents) / 100).toLocaleString('en-US')}` : ''} placeholder="type $" parse={(t) => (t.trim() === '' || /^~?\$?[\d,]+(\.\d+)?$/.test(t.trim()) ? t.trim() : undefined)} onSave={(v) => save({ typed_value_cents: v === '' ? null : Math.round(Number(v.replace(/[~$,]/g, '')) * 100) })} />
          )}
          <small>{lead.value.kind === 'priced' ? `priced${lead.value.jobs_priced > 1 ? ` · ${lead.value.jobs_priced} jobs` : ''}${lead.opp.typed_value_cents ? ` · typed ${money(Number(lead.opp.typed_value_cents), { whole: true })}` : ''}` : lead.value.kind === 'typed' ? 'typed' : lead.value.kind === 'range' ? 'range' : 'no value yet'}</small>
        </div>
        <div className="pval">
          <span className="lb">Likelihood</span>
          {canEdit ? <Edit className="pv-v cell" type="number" value={lead.opp.likelihood_pct ?? ''} placeholder={lead.likelihood !== null ? String(lead.likelihood) : '—'} onSave={(v) => save({ likelihood_pct: v === '' ? null : Number(v) })} /> : <span className="pv-v num">{lead.likelihood ?? '—'}%</span>}
          <small>
            {lead.opp.likelihood_pct !== null && lead.opp.likelihood_pct !== undefined ? 'this lead' : 'stage default'}
            {lead.quiet ? ` · halved: quiet ${lead.quietDays === null ? 'since it was made' : `${lead.quietDays} d`}` : ''}
          </small>
        </div>
        <div className="pval">
          <span className="lb">Anchor</span>
          {canEdit ? <Edit className="pv-v cell" type="date" value={lead.opp.anchor_date ?? ''} onSave={(v) => save({ anchor_date: v || null })} /> : <span className="pv-v num">{monthDay(lead.opp.anchor_date)}</span>}
          <small>
            {canEdit ? <Edit className="cell" value={lead.opp.anchor_type ?? ''} placeholder="which date" list="anchor-kinds" onSave={(v) => save({ anchor_type: v || null })} /> : lead.opp.anchor_type ?? 'no anchor type'}
            {lead.opp.anchor_date && lead.opp.anchor_date < todayStr && <Tag kind="off">past</Tag>}
            {lead.opp.anchor_confirmed_at ? <Tag kind="on">confirmed</Tag> : canEdit && lead.opp.anchor_date ? <button type="button" className="tag" onClick={() => void save({ anchor_confirmed_at: new Date().toISOString() })}>confirm</button> : null}
          </small>
        </div>
        <div className="pval">
          <span className="lb">Reply-by</span>
          {canEdit ? <Edit className="pv-v cell" type="date" value={lead.replyBy ?? ''} onSave={(v) => save({ reply_by: v || null, ...(v && !lead.nextTouch ? { next_touch_at: dayAfter(v) } : {}) })} /> : <span className="pv-v num">{lead.replyBy ? monthDay(lead.replyBy) : '—'}</span>}
          <small>{lead.replyBy === todayStr ? 'today' : lead.replyLate ? `${lead.replyLate} d late` : 'when the ask is due back'}</small>
        </div>
        <div className="pval">
          <span className="lb">Next touch</span>
          {canEdit ? <Edit className={`pv-v cell${lead.nextTouchLate ? ' late' : ''}`} type="date" value={lead.nextTouch ?? ''} onSave={(v) => save({ next_touch_at: v || null })} /> : <span className="pv-v num">{lead.nextTouch ? monthDay(lead.nextTouch, true) : '—'}</span>}
          <small>{lead.nextTouchLate ? `${lead.nextTouchLate} d late` : lead.replyBy && lead.nextTouch === dayAfter(lead.replyBy) ? 'day after reply-by' : lead.nextTouch ? monthDay(lead.nextTouch, true) : 'not set'}</small>
        </div>
      </div>
      <div className="panswers">
        <span>
          <span className="lb">First visit</span>
          <br />
          <Seg2 value={lead.opp.first_visit} options={p.answers.first_visit} disabled={!canEdit} onPick={(k) => save({ first_visit: k })} />
        </span>
        <span>
          <span className="lb">Design lane</span>
          <br />
          <Seg2 value={lead.opp.design_lane} options={p.answers.design_lane} disabled={!canEdit} onPick={(k) => save({ design_lane: k })} />
        </span>
        {p.answers.flags.length > 0 && (
          <span>
            <span className="lb">Also</span>
            <br />
            <span className="pchips">
              {p.answers.flags.map((f) => {
                const v = lead.opp.pipeline_flags?.[f.key];
                return (
                  <button key={f.key} type="button" className={`pchip${v === true ? ' on' : ''}${v === false ? ' no' : ''}`} disabled={!canEdit} title={v === undefined ? 'Not answered: tap for yes, again for no, again to clear' : v ? 'Yes' : 'No'} onClick={() => void save({ pipeline_flags: { ...(lead.opp.pipeline_flags ?? {}), [f.key]: v === undefined ? true : v === true ? false : undefined } })}>
                    {f.label}
                    {v === false ? ' · no' : ''}
                  </button>
                );
              })}
            </span>
          </span>
        )}
      </div>
    </Panel>
  );

  const nowPanel = (
    <Panel className={band.gold ? 'pnow gold' : 'pnow'}>
      <div className={`pwith${band.gold ? ' gold' : ''}`}>
        {band.words}
        {lead.daysOnStep !== null && lead.step && lead.outcome === 'open' && <small>{lead.daysOnStep} d on this step</small>}
      </div>
      {tap && <UndoRow tap={tap} onUndo={() => void taps.undo(lead.id)} />}
      {!tap && lead.step && lead.outcome === 'open' && (
        <>
          <div className="pstn">{lead.step.name}</div>
          <div className="small">
            <SeatTag p={p} step={lead.step} /> · {lead.readyRow ? 'ready · waiting on you' : stepDoneWords(p, lead)}
            {lead.dateStep && <span className="muted"> · landed: reply-by passed {lead.replyBy ? monthDay(lead.replyBy) : ''}</span>}
            {lead.step.note ? <span className="muted"> · {lead.step.note}</span> : ''}
          </div>
          <ReviewLine p={p} lead={lead} after={<small> · to review</small>} />
          <StepControls p={p} lead={lead} onTap={(t) => taps.remember(lead.id, t)} />
          <div className="phrow">
            <HandTo p={p} lead={lead} />
            <Discuss p={p} lead={lead} />
            {lead.handoffs.length > 0 && <small>Handed to {lead.handoffs.map((h) => (h.as_task ? 'you (a task)' : users.find((u) => u.id === h.to_user_id)?.name ?? 'someone')).join(', ')}</small>}
          </div>
          <StepBar p={p} lead={lead} />
        </>
      )}
      {!tap && !lead.step && lead.outcome === 'open' && (
        <div className="pctl">
          <Link to="/pipeline/place" className="btn p">
            Place it on a step
          </Link>
        </div>
      )}
      {!tap && lead.outcome !== 'open' && canEdit && (
        <div className="pctl">
          <BringBack lead={lead} onTap={(t) => taps.remember(lead.id, t)} />
          {lead.outcome === 'parked' && <small>{lead.returnTo ? `Returns to ${lead.returnTo.stage.name} · ${lead.returnTo.step.name}` : 'Return step: Pipeline manager to set'}</small>}
          {lead.outcome === 'lost' && <small>{lead.nextTouch ? `Re-approach ${monthDay(lead.nextTouch)}; clear the next touch if it is truly dead.` : 'No re-approach date.'}</small>}
          {lead.outcome === 'won' && <small>Won leads carry no open steps unless the Won stage has them.</small>}
        </div>
      )}
    </Panel>
  );

  const jobsPanel = (
    <Panel label="Jobs" right={canEdit ? <Btn small onClick={() => setAddJob(true)}>+ Job</Btn> : undefined}>
      {lead.jobs.length === 0 && (
        <small>
          No estimate yet{lead.value.kind === 'typed' ? ` · value is the typed ${money(lead.value.cents, { whole: true })}` : ''}.
        </small>
      )}
      {lead.jobs.length > 0 && (
        <div className="tblwrap">
          <table className="pjobs">
            <thead>
              <tr>
                <th>Job</th>
                <th>Build</th>
                {prices && <th className="right">Price</th>}
                <th className="right">Hours</th>
                <th className="right">Warnings</th>
                <th>Where it is</th>
              </tr>
            </thead>
            <tbody>
              {lead.jobs.map((j) => (
                <tr key={j.job.id} className={j.retired ? 'retired' : undefined}>
                  <td>
                    <Link to={`/jobs/${j.job.id}`}>
                      <b>{j.job.scope_name}</b>
                    </Link>
                    {j.retired && (
                      <>
                        {' '}
                        <span className="tag off" title="Retired: it counts nowhere, not in this lead's value (nothing is deleted). Bring it back on the job's Details tab.">retired</span>
                      </>
                    )}
                  </td>
                  <td>{j.builds.find((b) => b.is_base)?.name ?? '—'}</td>
                  {prices && (
                    <td className="right">
                      {j.res ? <Money cents={j.res.totals.price_cents} /> : <small>not priced</small>}
                      {j.res && j.res.totals.unverified_items > 0 && (
                        <>
                          {' '}
                          <Tag kind="un">{j.res.totals.unverified_items} unverified</Tag>
                        </>
                      )}
                    </td>
                  )}
                  <td className="right num">{j.res ? `${(Number(j.res.totals.labor_minutes) / 60).toFixed(1)} h` : '—'}</td>
                  <td className="right num">{j.res?.totals.warnings ?? '—'}</td>
                  <td>
                    <small>{j.stage?.name ?? '—'}</small>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {splitTo.length > 0 && (
        <small>
          Split off: {splitTo.map((l, i) => (
            <span key={l.id}>
              {i > 0 && ', '}
              <Link to={`/opportunities/${l.id}`}>{l.name}</Link>
              {l.stage ? ` in ${l.stage.name}` : ''}
            </span>
          ))}
          .
        </small>
      )}
    </Panel>
  );

  const activityPanel = (
    <Panel label={`Activity${phone ? ` · last ${Math.min(3, log.length)}` : ` · ${log.length}`}`}>
      {canEdit && <TouchBox p={p} lead={lead} />}
      <div className="plog">
        {log.length === 0 && <small>Nothing logged yet.</small>}
        {shownLog.map((a) => <LogEntry key={a.id} a={a} p={p} users={users} lit={litEntry === a.id} onUndo={a.id === mayUndo?.id ? undoFromLog : undefined} />)}
      </div>
      {phone && log.length > 3 && (
        <button type="button" className="linklike plink" onClick={() => setAllLog(!allLog)}>
          {allLog ? 'Last 3 only' : `All ${log.length} entries`}
        </button>
      )}
    </Panel>
  );

  const headerButtons = (
    <div className="pctl">
      {lead.opp.drive_folder && (
        <a className="btn sm" href={lead.opp.drive_folder} target="_blank" rel="noreferrer">
          Drive folder
        </a>
      )}
      {lead.opp.notion_project_url && (
        <a className="btn sm" href={lead.opp.notion_project_url} target="_blank" rel="noreferrer">
          Notion project
        </a>
      )}
      {canEdit && !lead.retired && (
        <Btn small onClick={() => setSplitting(true)} title="The client's rooms start deciding separately: two opportunities, each with its jobs and its share of the log">
          Split
        </Btn>
      )}
    </div>
  );
  const setRetired = (retire: boolean) => {
    if (retire && !window.confirm(`Retire ${lead.name}? It counts nowhere from now: not on the board, in no value, in no win rate. Nothing is deleted.`)) return;
    void rpc('retire_opportunity', { p_id: lead.id, p_note: null, p_retire: retire }).then(() => refresh('opportunity', 'activity'));
  };
  // a retired lead says so under its title, with the way back at full weight; retiring a live one is a rare act, at the foot
  const retiredLine = lead.retired && (
    <div className="pctl" style={{ marginTop: 6 }}>
      <Tag kind="off">retired{lead.opp.retired_note ? ` · ${lead.opp.retired_note}` : ''} · counts nowhere</Tag>
      {s.role === 'owner' && canEdit && (
        <Btn small title="Bring it back: it counts again" onClick={() => setRetired(false)}>
          Bring back
        </Btn>
      )}
    </div>
  );
  const retireLink = s.role === 'owner' && canEdit && !lead.retired && (
    <small>
      <button type="button" className="linklike" title="A test row or a duplicate: it counts nowhere from now, on the board, in no value, in no win rate. Nothing is deleted." onClick={() => setRetired(true)}>
        Retire this lead
      </button>
    </small>
  );
  const header = (
    <div className="pohead">
      <div>
        {canEdit ? (
          <h1 className="poname">
            <Edit className="poname-edit" value={lead.name} placeholder="the lead's name" onSave={(v) => save({ name: v || lead.name })} />
          </h1>
        ) : (
          <h1 className="poname">{lead.name}</h1>
        )}
        <div className="small">
          <ContactsLine p={p} lead={lead} canEdit={canEdit} />
          {(lead.opp.source || lead.opp.vertical) && <> · {[lead.opp.source, lead.opp.vertical].filter(Boolean).join(' · ')}</>}
          {splitFrom && (
            <>
              {' '}
              · split {monthDay(lead.opp.split_at)} from <Link to={`/opportunities/${splitFrom.id}`}>{splitFrom.name}</Link>
            </>
          )}
        </div>
        {retiredLine}
      </div>
      {!phone && headerButtons}
    </div>
  );

  // a parked lead keeps its stage: the held stage reads dimmed with "Parked · date" under it (Iris, 2026-10-22)
  const track = (
    <div className="ptrack" role="tablist" aria-label="Stage">
      {p.working.map((st, i) => {
        const cur = lead.stage?.id === st.id && lead.outcome === 'open';
        const held = lead.stage?.id === st.id && lead.outcome === 'parked';
        const curIndex = p.working.findIndex((x) => x.id === lead.stage?.id);
        const doneStage = lead.outcome === 'won' || (curIndex > i && lead.outcome === 'open');
        return (
          <button key={st.id} type="button" className={`pseg${cur ? ' cur' : ''}${held ? ' held' : ''}${doneStage ? ' done' : ''}${asking?.stage.id === st.id ? ' asked' : ''}`} disabled={!canEdit || cur} title={canEdit ? `Move to ${st.name}` : st.name} onClick={() => void trackMove(st.key)}>
            {st.name}
            {held && <small className="num">Parked · {lead.nextTouch ? monthDay(lead.nextTouch) : 'no date'}</small>}
          </button>
        );
      })}
      {p.wonStage && (
        <button type="button" className={`pseg${lead.outcome === 'won' ? ' cur' : ''}${asking?.stage.id === p.wonStage.id ? ' asked' : ''}`} disabled={!canEdit || lead.outcome === 'won'} onClick={() => void trackMove(p.wonStage!.key)}>
          {p.wonStage.name}
        </button>
      )}
      <span className="pgap" />
      {p.parkedStage && (
        <button type="button" className={`pseg off${lead.outcome === 'parked' ? ' cur' : ''}`} disabled={!canEdit || lead.outcome === 'parked'} onClick={() => void trackMove(p.parkedStage!.key)}>
          Park
        </button>
      )}
      {p.lostStage && (
        <button type="button" className={`pseg off${lead.outcome === 'lost' ? ' cur' : ''}`} disabled={!canEdit || lead.outcome === 'lost'} onClick={() => void trackMove(p.lostStage!.key)}>
          Lost
        </button>
      )}
    </div>
  );
  const ask = asking && (
    <div className="pask" role="alertdialog" aria-label={`${asking.kind === 'park' ? 'Park' : asking.kind === 'lost' ? 'Lost' : 'Move to ' + asking.stage.name}?`}>
      <span>
        {asking.kind === 'won' ? (
          <>
            Mark it <b>{asking.stage.name}</b>? Next touch and reply-by clear.
          </>
        ) : asking.kind === 'park' ? (
          <>
            <b>Park</b> it until <input type="date" value={askDate} onChange={(e) => setAskDate(e.target.value)} aria-label="Re-approach date" /> <small>the re-approach date is the next touch; it keeps its step</small>
          </>
        ) : asking.kind === 'lost' ? (
          <>
            <b>Lost</b> · <input type="text" value={askWhy} placeholder="The reason" onChange={(e) => setAskWhy(e.target.value)} aria-label="Lost reason" />{' '}
            <small>{lostRetouch(lostWeeks) ? `re-approach ${monthDay(lostRetouch(lostWeeks))}` : 'no re-approach'}</small>
          </>
        ) : (
          <>
            Move to <b>{asking.stage.name}</b>?{asking.step ? ` It lands on ${asking.step.name}.` : ''}
          </>
        )}
      </span>
      <span className="pctl">
        <Btn small primary disabled={(asking.kind === 'park' && !askDate) || (asking.kind === 'lost' && !askWhy.trim())} onClick={() => void move()}>
          {asking.kind === 'won' ? `Mark ${asking.stage.name}` : asking.kind === 'park' ? 'Park until then' : asking.kind === 'lost' ? 'Mark lost' : 'Move'}
        </Btn>
        <Btn small onClick={() => setAsking(null)}>
          Cancel
        </Btn>
      </span>
    </div>
  );

  const extras = (
    <Fold open={folds.isOpen('more')} onToggle={() => folds.toggle('more')} label="Sources and files" className="pmore">
      <Sources lead={lead} canEdit={canEdit} log={log} onShowEntry={showEntry} />
      <div style={{ marginTop: 10 }}>
        <div className="lb">Files</div>
        <FileList attachedType="opportunity" attachedId={lead.id} />
      </div>
      {canEdit && (
        <div style={{ marginTop: 10 }}>
          <div className="lb">Source, vertical, Drive folder</div>
          <div className="form">
            <label className="field">
              <span>Source</span>
              <select value={lead.opp.source ?? ''} onChange={(e) => void save({ source: e.target.value || null })}>
                <option value="">—</option>
                {[...new Set([...s.setting<string[]>('opportunity_sources', []), ...(lead.opp.source ? [String(lead.opp.source)] : [])])].map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Vertical</span>
              <select value={lead.opp.vertical ?? ''} onChange={(e) => void save({ vertical: e.target.value || null })}>
                <option value="">—</option>
                {[...new Set([...s.setting<string[]>('opportunity_verticals', []), ...(lead.opp.vertical ? [String(lead.opp.vertical)] : [])])].map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            </label>
            <label className="field wide">
              <span>Drive folder</span>
              <Edit value={lead.opp.drive_folder ?? ''} placeholder="https://drive.google.com/…" onSave={(v) => save({ drive_folder: v || null })} />
            </label>
            <label className="field wide">
              <span>Notion project</span>
              <Edit value={lead.opp.notion_project_url ?? ''} placeholder="the delivery record, once won" onSave={(v) => save({ notion_project_url: v || null })} />
            </label>
            {lead.opp.notion_stage && (
              <label className="field">
                <span>Came from Notion as</span>
                <span className="val">{lead.opp.notion_stage}</span>
              </label>
            )}
          </div>
        </div>
      )}
    </Fold>
  );

  return (
    <div className={`stack popp${lead.retired ? ' retired' : ''}`}>
      {header}
      {track}
      {ask}
      {phone ? (
        <>
          {nowPanel}
          {values}
          {jobsPanel}
          {activityPanel}
          <Panel>{headerButtons}</Panel>
          {extras}
          {retireLink}
        </>
      ) : (
        <div className="pogrid">
          <div className="stack">
            {nowPanel}
            {values}
            {jobsPanel}
            {extras}
            {retireLink}
          </div>
          <div>{activityPanel}</div>
        </div>
      )}
      <datalist id="anchor-kinds">
        <option value="Build complete" />
        <option value="Deposit in" />
        <option value="Retainer signed" />
      </datalist>
      {addJob && <NewOpportunity opportunityId={lead.id} onClose={() => setAddJob(false)} />}
      {splitting && <SplitDialog p={p} lead={lead} log={log} onClose={() => setSplitting(false)} onSplit={(newId) => nav(`/opportunities/${newId}`)} />}
    </div>
  );
}

/**
 * The lead's steps as one bar of boxes (SM4, SM5 and SM8, Spencer 2026-10-24; replaced Iris's numbered list of 2026-10-22):
 * one box per step the lead has (the engine's step list; a step that will never apply is not drawn), done filled, the
 * one on now lit, a step whose condition is not settled yet dashed, to do empty. The step on now has its name under its
 * own box; a tap (or a hover at the desk) on another box shows that name there instead. Under it the returning-client
 * line the old list carried on its skipped row, and the stage line.
 */
function StepBar({ p, lead }: { p: Pipeline; lead: Lead }) {
  const [shown, setShown] = useState<string | null>(null);
  void p;
  if (!lead.stage || lead.stepList.length === 0) return null;
  const rows = lead.stepList.filter((r) => r.status !== 'skipped');
  const skippedNew = lead.stepList.find((r) => r.status === 'skipped' && r.condition?.when.new_lead !== undefined);
  const cur = rows.findIndex((r) => r.status === 'current');
  const pick = shown !== null ? rows.findIndex((r) => r.step.id === shown) : -1;
  const at = pick >= 0 ? pick : cur;
  const n = rows.length;
  const named = at >= 0 ? rows[at]! : null;
  const seat = named ? seatWords(p, named.step) : null;
  // the name sits under its box: left-aligned from the box on, or right-aligned up to it when the box is in the right half
  const right = at >= n / 2;
  const words = (r: (typeof rows)[number]) => (r.status === 'done' ? 'done' : r.status === 'current' ? 'on now' : r.status === 'pending' ? `waits on ${r.condition?.label?.toLowerCase() ?? 'its condition'}` : 'to do');
  return (
    <div className="psmini">
      <div className="lb">Steps · {cur >= 0 ? `${cur + 1} of ${n}` : `${n}`}</div>
      <div className="pstepbar" style={{ gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))` }} onMouseLeave={() => setShown(null)}>
        {rows.map((r) => (
          <button
            key={r.step.id}
            type="button"
            className={`bx ${r.status}${shown === r.step.id ? ' pick' : ''}`}
            title={`${r.step.name} · ${words(r)}`}
            aria-label={`${r.step.name} · ${words(r)}`}
            aria-pressed={shown === r.step.id}
            onMouseEnter={() => setShown(r.step.id)}
            onFocus={() => setShown(r.step.id)}
            onBlur={() => setShown(null)}
            onClick={() => setShown(shown === r.step.id ? null : r.step.id)}
          />
        ))}
        {named && (
          <span className={`nm${right ? ' right' : ''}`} style={{ gridColumn: right ? `1 / ${at + 2}` : `${at + 1} / -1` }}>
            {named.step.name}
            <small>
              {words(named)}
              {named.status === 'current' && seat?.words ? ` · ${seat.words}` : ''}
            </small>
          </span>
        )}
      </div>
      {skippedNew && lead.primary && (
        <small className="pstl-stage">
          {skippedNew.step.name}: skipped{skippedNew.words ? ` · ${skippedNew.words}` : ''} ·{' '}
          <Link to={`/people?contact=${lead.primary.id}`} className="plink">
            {contactName(lead.primary)} · {lead.history.earlier} earlier lead{lead.history.earlier === 1 ? '' : 's'}
            {lead.history.won ? ` · ${lead.history.won} won` : ''}
            {lead.history.lost ? ` · ${lead.history.lost} lost` : ''}
          </Link>
        </small>
      )}
      <small className="pstl-stage">
        <Link to={`/pipeline/stage/${lead.stage.key}`} className="plink">
          Every lead at {lead.stage.name}
        </Link>
      </small>
    </div>
  );
}

function stepDoneWords(p: Pipeline, lead: Lead): string {
  const st = lead.step!;
  switch (st.done_rule) {
    case 'seat_ticks':
      return seatWords(p, st).you ? 'done when you tick it' : 'done when the seat ticks it';
    case 'owner_approves':
      return 'the seat marks it ready, your Approve finishes it';
    case 'event':
      return `done when ${p.events.find((e) => e.key === st.event_key)?.label?.toLowerCase() ?? st.event_key}`;
    case 'client_acts':
      return 'done when the client acts';
  }
}

/** A two-way (or more) choice drawn as segments. */
function Seg2({ value, options, onPick, disabled }: { value: string | null | undefined; options: { key: string; label: string }[]; onPick: (k: string | null) => void; disabled?: boolean }) {
  return (
    <span className="pseg2">
      {options.map((o) => (
        <button key={o.key} type="button" className={o.key === value ? 'on' : ''} disabled={disabled} onClick={() => onPick(o.key === value ? null : o.key)}>
          {o.label}
        </button>
      ))}
      {options.length === 0 && <small>no choices in the pipeline_answers setting</small>}
      {options.length > 0 && !value && <small className="pnotset">not decided</small>}
    </span>
  );
}

function ContactsLine({ p, lead, canEdit }: { p: Pipeline; lead: Lead; canEdit: boolean }) {
  const [adding, setAdding] = useState(false);
  const contacts = useRows('contact', { order: ['last_name', 'company'], enabled: adding });
  const links = useRows('opportunity_contact', { eq: { opportunity_id: lead.id } });
  void p;
  return (
    <>
      {lead.contacts.map((c, i) => (
        <span key={c.id}>
          {i > 0 && ' · '}
          <Link to={`/people?contact=${c.id}`}>{contactName(c)}</Link>
          {c.id === lead.primary?.id && lead.contacts.length > 1 && <span className="pown" style={{ marginLeft: 4 }}>primary</span>}
          {canEdit && c.id !== lead.primary?.id && (
            <button type="button" className="linklike" style={{ marginLeft: 4, fontSize: 12 }} title="Make this the primary contact" onClick={() => void saveLead(lead, { primary_contact_id: c.id })}>
              make primary
            </button>
          )}
        </span>
      ))}
      {canEdit && !adding && (
        <>
          {lead.contacts.length > 0 && ' · '}
          <button type="button" className="linklike plink" onClick={() => setAdding(true)}>
            + contact
          </button>
        </>
      )}
      {adding && (
        <select
          autoFocus
          value=""
          onChange={async (e) => {
            const cid = e.target.value;
            setAdding(false);
            if (!cid) return;
            await insertRow('opportunity_contact', { opportunity_id: lead.id, contact_id: cid, role: 'Client' });
            if (!lead.primary) await saveLead(lead, { primary_contact_id: cid });
          }}
          onBlur={() => setAdding(false)}
        >
          <option value="">Add a contact…</option>
          {contacts.rows
            .filter((c) => !links.rows.some((l) => l.contact_id === c.id))
            .map((c) => (
              <option key={c.id} value={c.id}>
                {contactName(c)}
              </option>
            ))}
        </select>
      )}
    </>
  );
}

/** Log a touch or a note: the channel buttons from the touch_channels setting, in or out. */
function TouchBox({ p, lead }: { p: Pipeline; lead: Lead }) {
  const s = useSession();
  const [text, setText] = useState('');
  const [channel, setChannel] = useState<string | null>(null);
  const [dir, setDir] = useState<'in' | 'out'>('out');
  const channels = s.setting<string[]>('touch_channels', []);
  const send = async (kind: 'touch' | 'note') => {
    const body = text.trim();
    if (!body && kind === 'note') return;
    await logTouch(lead.id, { kind, channel: kind === 'touch' ? channel : null, direction: kind === 'touch' ? dir : null, body: body || `${channel ?? 'Touch'} ${dir}`, byUserId: s.user?.id ?? null, contactId: lead.primary?.id ?? null });
    setText('');
    setChannel(null);
  };
  void p;
  return (
    <div className="pcap">
      <input type="text" placeholder="Log a touch or note" value={text} onChange={(e) => setText(e.target.value)} aria-label="Touch or note" onKeyDown={(e) => e.key === 'Enter' && void send(channel ? 'touch' : 'note')} />
      <span className="pchips">
        {channels.map((c) => (
          <button key={c} type="button" className={`pchip${channel === c ? ' on' : ''}`} onClick={() => setChannel(channel === c ? null : c)}>
            {c}
          </button>
        ))}
        <span className="pseg2" title="Out: we reached them. In: they reached us.">
          <button type="button" className={dir === 'out' ? 'on' : ''} onClick={() => setDir('out')}>
            Out
          </button>
          <button type="button" className={dir === 'in' ? 'on' : ''} onClick={() => setDir('in')}>
            In
          </button>
        </span>
        <Btn small primary disabled={!channel && !text.trim()} onClick={() => void send(channel ? 'touch' : 'note')}>
          {channel ? 'Log touch' : 'Add note'}
        </Btn>
      </span>
    </div>
  );
}

/** What a tap's entry puts the lead back to, in words: "Scope · Designer drafts the Scope Sheet", or the outcome it had. */
function backTo(p: Pipeline, prev: Row): string {
  if (prev.outcome && prev.outcome !== 'open') return prev.outcome === 'not_a_lead' ? 'Not a lead' : String(prev.outcome).replace(/^./, (c) => c.toUpperCase());
  const stage = p.stages.find((x) => x.id === prev.pipeline_stage_id)?.name;
  const step = p.steps.find((x) => x.id === prev.current_step_id)?.name;
  return [stage, step].filter(Boolean).join(' · ') || 'not placed';
}

export function LogEntry({ a, p, users, onUndo, lit }: { a: Row; p: Pipeline; users: Row[]; onUndo?: () => Promise<void>; /** Lit for a moment after a client page's answer chip opened it. */ lit?: boolean }) {
  const [open, setOpen] = useState(false);
  const [asking, setAsking] = useState(false);
  const system = a.kind !== 'touch' && a.kind !== 'note';
  const who = a.by_name ?? users.find((u) => u.id === a.by_user_id)?.name ?? null;
  const label =
    a.kind === 'touch'
      ? `${a.channel ?? 'Touch'}${a.direction ? ` ${a.direction}` : ''}${who ? ` · ${who}` : ''}`
      : a.kind === 'note'
        ? `Note${who ? ` · ${who}` : ''}`
        : a.kind === 'step'
          ? 'Step'
          : a.kind === 'stage'
            ? 'Stage'
            : a.kind === 'handoff'
              ? 'Hand-off'
              : a.kind === 'split'
                ? 'Split'
                : a.kind === 'concept_board'
                  ? 'Concept Board'
                  : 'Import';
  const body = String(a.body ?? '').replace(/\*\*/g, '');
  const to = a.kind === 'split' && a.payload?.to ? p.byId.get(String(a.payload.to)) : a.kind === 'split' && a.payload?.from ? p.byId.get(String(a.payload.from)) : null;
  return (
    <div id={`log-${a.id}`} className={`ple${system ? ' sys' : ''}${lit ? ' lit' : ''}`}>
      <span className="pdt num" title={longDate(a.at)}>
        {monthDay(a.at)}
      </span>
      <span className="pch">{label}</span>
      <span className={`pbody${body.length > 160 && !open ? ' clamp' : ''}`} onClick={() => body.length > 160 && setOpen(!open)} title={body.length > 160 ? (open ? 'Tap to fold' : 'Tap to read it all') : undefined}>
        {body}
        {to && (
          <>
            {' '}
            · <Link to={`/opportunities/${to.id}`}>{to.name}</Link>
          </>
        )}
        {onUndo && !asking && (
          <button type="button" className="btn sm plogundobtn" onClick={(e) => (e.stopPropagation(), setAsking(true))}>
            Undo
          </button>
        )}
      </span>
      {onUndo && asking && (
        <div className="pundo plogundo">
          <span>Undo this? It goes back to {backTo(p, a.payload.prev)}.</span>
          <span className="pctl">
            <Btn small primary onClick={() => void onUndo().then(() => setAsking(false))}>
              Undo
            </Btn>
            <Btn small onClick={() => setAsking(false)}>
              Keep
            </Btn>
          </span>
        </div>
      )}
    </div>
  );
}

/**
 * Where the scope came from (the 2026-10 sources), kept in the fold. A client page link is a kind of source (SM6 and SM7,
 * Spencer 2026-10-24), pinned at the top: name, date, Open, Copy link, and beside it one chip with the latest answer
 * state read from the activity log where the page's Send posts ("no answer yet", "answered 10-24 · 2 picks"); a tap on
 * the chip opens that log entry.
 */
function Sources({ lead, canEdit, log, onShowEntry }: { lead: Lead; canEdit: boolean; log: Row[]; onShowEntry: (id: string) => void }) {
  const sources = useRows('opportunity_source', { eq: { opportunity_id: lead.id }, order: ['received_at'] });
  const scope = useRows('scope_row', { eq: { opportunity_id: lead.id } });
  const [newSource, setNewSource] = useState({ kind: 'drawing', title: '', url: '' });
  const pending = scope.rows.filter((r) => r.outcome === 'pending').length;
  const pages = sources.rows.filter((s) => s.kind === 'client_page');
  const others = sources.rows.filter((s) => s.kind !== 'client_page');
  // the latest Send for a page: the newest concept_board entry whose slug is the page's
  const answerOf = (src: Row) => {
    const slug = slugOf(src.url);
    const hits = log.filter((a) => a.kind === 'concept_board' && String((a.payload as Row | null)?.slug ?? '') === slug && slug);
    const a = hits.sort((x, y) => String(y.at).localeCompare(String(x.at)))[0];
    if (!a) return null;
    const answers = ((a.payload as Row | null)?.answers ?? {}) as Row;
    const picks = Array.isArray(answers.picks) ? (answers.picks as unknown[]).length : answers.picks && typeof answers.picks === 'object' ? Object.keys(answers.picks as object).length : Object.keys(answers).filter((k) => k !== 'savedAt' && k !== 'notes').length;
    return { entry: a, words: `answered ${monthDay(a.at)}${picks ? ` · ${picks} ${picks === 1 ? 'pick' : 'picks'}` : ''}` };
  };
  const copy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast('Link copied.');
    } catch {
      window.prompt('Copy the link:', url);
    }
  };
  const kindOk = newSource.kind !== 'client_page' || /^https?:\/\//i.test(newSource.url.trim());
  return (
    <div>
      <div className="lb">
        <span>Sources · where the scope came from</span>
        {pending > 0 && <Tag kind="un">{pending} scope rows open</Tag>}
      </div>
      {sources.rows.length === 0 && <small>None yet.</small>}
      {pages.map((src) => {
        const ans = answerOf(src);
        return (
          <div key={src.id} className="pln ppage">
            <span>
              <b>{src.title || 'Client page'}</b>
              <br />
              <small>
                client page · {src.received_at ? monthDay(src.received_at) : '—'}
                {scope.rows.some((r) => r.source_id === src.id) ? ` · ${scope.rows.filter((r) => r.source_id === src.id).length} scope rows` : ''}
              </small>
            </span>
            <span className="ppage-r">
              {ans ? (
                <button type="button" className="tag on" title="Open the log entry with the client's answers" onClick={() => onShowEntry(ans.entry.id as string)}>
                  {ans.words}
                </button>
              ) : (
                <Tag kind="off">no answer yet</Tag>
              )}
              <span className="pctl">
                <a className="btn sm" href={String(src.url ?? '#')} target="_blank" rel="noreferrer">
                  Open
                </a>
                <Btn small onClick={() => void copy(String(src.url ?? ''))}>
                  Copy link
                </Btn>
                {canEdit && (
                  <Btn small onClick={() => window.confirm(`Remove the client page "${src.title || src.url}"?`) && void deleteRow('opportunity_source', src.id)}>
                    ×
                  </Btn>
                )}
              </span>
            </span>
          </div>
        );
      })}
      {others.map((src) => (
        <div key={src.id} className="pln">
          <span>
            <b>{src.title || kindWords(src.kind)}</b>
            <br />
            <small>
              {kindWords(src.kind)} · {longDate(src.received_at)} · {scope.rows.filter((r) => r.source_id === src.id).length} scope rows
            </small>
          </span>
          <span>
            <label className="check">
              <input type="checkbox" checked={!!src.extracted} disabled={!canEdit} onChange={(e) => void updateRow('opportunity_source', src.id, { extracted: e.target.checked })} /> extracted
            </label>
            {canEdit && (
              <Btn small onClick={() => window.confirm(`Remove the source "${src.title || src.kind}"?`) && void deleteRow('opportunity_source', src.id)}>
                ×
              </Btn>
            )}
          </span>
        </div>
      ))}
      {canEdit && (
        <div className="row mid wrap" style={{ marginTop: 8, gap: 6 }}>
          <select value={newSource.kind} onChange={(e) => setNewSource({ ...newSource, kind: e.target.value })}>
            {SOURCE_KINDS.map((k) => (
              <option key={k} value={k}>
                {kindWords(k)}
              </option>
            ))}
          </select>
          <input type="text" className="grow" placeholder="What it is" value={newSource.title} onChange={(e) => setNewSource({ ...newSource, title: e.target.value })} />
          {newSource.kind === 'client_page' && <input type="url" className="grow" placeholder="https://… the page's address" value={newSource.url} onChange={(e) => setNewSource({ ...newSource, url: e.target.value })} />}
          <Btn
            small
            disabled={!newSource.title.trim() || !kindOk}
            onClick={async () => {
              await insertRow('opportunity_source', { opportunity_id: lead.id, kind: newSource.kind, title: newSource.title.trim(), received_at: new Date().toISOString(), ...(newSource.kind === 'client_page' ? { url: newSource.url.trim() } : {}) });
              setNewSource({ kind: 'drawing', title: '', url: '' });
            }}
          >
            + Source
          </Btn>
        </div>
      )}
    </div>
  );
}
